import { Box, Grid, Paper, Button } from '@mui/material';
import styles from './simulator-v2-interaction-history-tab.module.scss';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import SimulatorV2InteractionHistoryModal from './simulator-v2-interaction-history-modal';
import { getSelectedContact } from '../../+state/simulatorV2.slice';
import { useSelector } from 'react-redux';

import {
  CcfChatIcon,
  CcfEmailIcon,
  CcfSmsIcon,
  CcfPhoneOutboundIcon,
  CcfEditPenIcon,
  CcfInstaIcon,
  CcfXIcon,
  CcfFacebookIcon,
  CcfLinkedinIcon,
  CcfTrashIcon,
  CcfTooltip,
} from '@nice-ccf/ui-controls';
import { generateDate } from '../../utils/utils';

/* eslint-disable-next-line */
export interface SimulatorV2InteractionHistoryTabProps {
  setUpdatedContact: any;
  updatedContact: any;
}

const Item = styled(Paper)(({ theme }) => ({
  textAlign: 'left',
  borderRadius: '0px',
  boxShadow: 'none',
}));

/**
 * used to show the Interaction History Tab
 * @param props - SimulatorV2InteractionHistoryTabProps
 * @example ` <SimulatorV2InteractionHistoryTab />`
 */
export function SimulatorV2InteractionHistoryTab(
  props: SimulatorV2InteractionHistoryTabProps
) {
  const { setUpdatedContact, updatedContact } = props;
  const selectedContact: any = useSelector(getSelectedContact);
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [interactionHistoryData, setInteractionHistoryData] = useState(
    selectedContact?.interactionHistoryList
  );
  const [customerData, setCustomerData] = useState(selectedContact?.customer);
  const [selectedData, setSelectedData] = useState();

  /**
   * Handles the action of adding a new item by setting the state to show the modal for creating a new item.
   * This function updates the `showNewItemModal` state to `true`, triggering the display of the modal.
   * @example
   */
  const handleAddNewItem = () => {
    setShowNewItemModal(true);
  };

  /**
   * Handles the action of editing an item.
   *
   * @param item - The item to be edited. This can be of any type.
   * @returns void
   *
   * This function sets the selected data to the provided item and
   * displays the edit item modal by updating the relevant state variables.
   * @example
   */
  const handleEditItem = (item: any): void => {
    setSelectedData(item);
    setShowEditItemModal(true);
  };

  /**
   * Handles the closing of modals in the interaction history tab.
   * This function sets the state to hide both the "New Item" and "Edit Item" modals.
   * @example
   */
  const handleClose = () => {
    setShowNewItemModal(false);
    setShowEditItemModal(false);
  };

  useEffect(() => {
    setUpdatedContact({
      ...updatedContact,
      lastUpdatedDate: generateDate(),
      interactionHistoryList: interactionHistoryData,
    });
    localStorage.setItem(
      'interactionHistoryLS',
      JSON.stringify(interactionHistoryData)
    );
  }, [interactionHistoryData]);

  /**
   * Handles the save operation for interaction history data.
   @example
   * @param newItem - The interaction item to be saved. It can either be a new item or an existing one to update.
   */
  const handleSave = (newItem: any, type: string) => {
    if (type === 'new') {
      if (!newItem.media) {
        newItem.media = 'Email';
      }
      if (!newItem.status) {
        newItem.status = 'open';
      }
      setInteractionHistoryData((prevData: any) => {
        const lastId =
          prevData.length > 0 ? prevData[prevData.length - 1].interactionId : 0;
        const newObj = { ...newItem, interactionId: lastId + 1 };
        return [...prevData, newObj];
      });
    } else {
      setInteractionHistoryData((prevData: any) => {
        const updatedData = prevData.map((item: any) =>
          item.interactionId === newItem.interactionId ? newItem : item
        );
        return updatedData;
      });
    }

    setShowNewItemModal(false);
    setShowEditItemModal(false);
  };

  /**
   * Handles the update of customer data by merging the new item with the previous data.
   *@example
   * @param newItem - An object containing the new data to be merged with the existing customer data.
   * @returns The updated customer data after merging the new item.
   */
  const handleUpdatedData = (newItem: any) => {
    setCustomerData((prevData: any) => {
      const updatedData = { ...prevData, ...newItem };
      return updatedData;
    });
  };

  /**@example */
  const handleDeleteItem = (item: any, index: number) => {
    setInteractionHistoryData((prevData: any) => {
      const updatedData = prevData.filter(
        (i: any, idx: number) => idx !== index
      );
      return updatedData;
    });
  };

  return (
    <Box component="section" className={styles.interactionHistoryContainer}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={8}>
          <Item className={styles.interactionHistoryItem}>
            Interaction History
          </Item>
        </Grid>
        <Grid item xs={12} sm={12} md={2}>
          <Item className={styles.addNewItem} key="addNewItem">
            <Button
              variant="outlined"
              className={styles.addNewBtn}
              onClick={handleAddNewItem}
            >
              Add New
            </Button>
            {showNewItemModal && (
              <SimulatorV2InteractionHistoryModal
                isOpen={showNewItemModal}
                handleClose={handleClose}
                modalHeader="Add New Interaction"
                modalData={interactionHistoryData}
                customerData={customerData}
                onCustomerDataSave={handleUpdatedData}
                modalDataType="new"
                onSave={handleSave}
              />
            )}
          </Item>
        </Grid>
        <Grid item xs={2} sm={2} md={2}></Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        className={styles.interactionHistoryDataContainer}
      >
        {interactionHistoryData?.length > 0 &&
          interactionHistoryData?.map((item: any, index: any) => (
            <Grid item xs={12} sm={12} md={5} key={item.interactionId}>
              <Item className={styles.interactionHistoryDataItem}>
                <Grid container spacing={1}>
                  <Grid item xs={1} sm={1} md={1}>
                    {item.media === 'Email' && <CcfEmailIcon />}
                    {item.media === 'SMS' && <CcfSmsIcon />}
                    {item.media === 'Voice' && <CcfPhoneOutboundIcon />}
                    {item.media === 'Chat' && <CcfChatIcon />}
                    {item.media === 'Instagram' && <CcfInstaIcon />}
                    {item.media === 'X' && <CcfXIcon />}
                    {item.media === 'Facebook' && <CcfFacebookIcon />}
                    {item.media === 'LinkedIn' && <CcfLinkedinIcon />}
                  </Grid>
                  <Grid item xs={5} sm={5} md={5} className={styles.callNumber}>
                    {item.media === 'Email'
                      ? customerData?.emailId
                      : customerData?.phoneNo}
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    md={6}
                    className={styles.editBtn}
                    key={item.interactionId}
                  >
                    <CcfTooltip title="Delete">
                      <Button
                        style={{ minWidth: '1rem', padding: '0px' }}
                        onClick={() => handleDeleteItem(item, index)}
                      >
                        <CcfTrashIcon
                          style={{ fontSize: '20px' }}
                          fillColor="#000000"
                        />
                      </Button>
                    </CcfTooltip>
                    <CcfTooltip title="Edit">
                      <Button
                        style={{
                          minWidth: '1rem',
                          padding: '0px',
                          height: '1.5rem',
                        }}
                        onClick={() => handleEditItem(item)}
                      >
                        <CcfEditPenIcon
                          viewBox="3 0 20 20"
                          fillColor="#000000"
                        />
                      </Button>
                    </CcfTooltip>
                    {showEditItemModal && (
                      <SimulatorV2InteractionHistoryModal
                        isOpen={showEditItemModal}
                        handleClose={handleClose}
                        modalHeader="Edit Interaction"
                        modalData={selectedData}
                        customerData={customerData}
                        onCustomerDataSave={handleUpdatedData}
                        modalDataType="edit"
                        onSave={handleSave}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid xs={12} sm={12} md={12} className={styles.dataItemText}>
                    {item.contactRequest}
                  </Grid>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    className={styles.dataItemAgent}
                  >
                    {item.skill}
                  </Grid>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    className={
                      item.status === 'open'
                        ? styles.dataItemStatus
                        : styles.dataItemStatusClosed
                    }
                  >
                    {item.status}
                  </Grid>
                </Grid>
              </Item>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
}

export default SimulatorV2InteractionHistoryTab;
